/**
 * IndexedDB Utility for Flash-Read
 * Provides persistent storage for reading progress, book data, and covers
 *
 * Database: Flash-ReadDB
 * Object Stores:
 * - reading_progress: Reading progress per book
 * - book_covers: Cached cover images
 * - books: Full book data for imported books (chapters with content)
 */

// Database configuration
const DB_NAME = 'Flash-ReadDB';
const DB_VERSION = 7; // Bumped for reading_sessions_local and daily_challenges_local stores

// Store names
export const STORES = {
  PROGRESS: 'reading_progress',
  COVERS: 'book_covers',
  BOOKS: 'books', // NEW: store full ProcessedBookResult for imported books
  DAILY_READING_STATS: 'daily_reading_stats', // NEW: daily aggregates
  USER_PROFILE: 'user_profile', // NEW: local user profile store
  USER_ACHIEVEMENTS_LOCAL: 'user_achievements_local', // NEW: local achievements store
  // Ultra sync stores
  READING_SESSIONS: 'reading_sessions_local',
  DAILY_CHALLENGES_LOCAL: 'daily_challenges_local',
} as const;

// ============================================================================
// Type Definitions
// ============================================================================

import { ContentSection } from '../types/content';
import { ProcessedBookResult } from '../types/content';
import { ReadingSessionLocal, DailyChallengeLocal } from '../types/membership';

/**
 * Chapter progress tracking
 */
export interface ChapterProgress {
  chapterId: string;
  chapterTitle: string;
  wordIndex: number;
  completed: boolean;
  lastReadAt: string;
}

/**
 * Minimal chapter info for display (no content)
 */
export interface ChapterInfo {
  chapterId: string;
  chapterTitle: string;
  wordCount: number;
}

/**
 * Enhanced reading progress per book
 */
export interface BookReadingProgress {
  bookId: string;
  bookTitle: string;
  currentChapterId: string;
  currentChapterTitle: string;
  currentWordIndex: number;
  totalWordsRead: number;
  lastReadAt: string;
  chapterProgress: ChapterProgress[];
  // Optional metadata for display without separate catalog lookup:
  totalChapters?: number;
  coverUrl?: string;
  categoryId?: string;
  // NEW: extended metadata
  totalWords?: number;
  author?: string;
  chapters?: ChapterInfo[]; // minimal chapter list for UI
  sections?: ContentSection[]; // section metadata (contentPath) for library stories to reload
  completed?: boolean; // true when the book is fully read
}

/**
 * Daily reading statistics aggregate
 */
export interface DailyReadingStat {
  date: string; // YYYY-MM-DD
  userId?: string;
  totalSeconds: number;
  totalWords: number;
}

/**
 * Book data storage for imported books (full content)
 * This is essentially a ProcessedBookResult with an id
 */
export interface StoredProcessedBook extends ProcessedBookResult {
  bookId: string; // unique identifier (matches BookReadingProgress.bookId)
  id: string; // duplicate for BookCard compatibility
  totalChapters: number; // number of chapters for display
  coverUrl?: string; // optional cover image
}

/**
 * Bookmark for saving positions
 */
export interface Bookmark {
  id: string;
  bookId: string;
  chapterId: string;
  position: number;              // Character position
  createdAt: string;
  note?: string;
}

// ============================================================================
// Database Initialization
// ============================================================================

let dbInstance: IDBDatabase | null = null;

/**
 * Reset the database instance (for testing)
 */
export function resetDBInstance(): void {
  dbInstance = null;
}

/**
 * Initialize the IndexedDB database
 * Creates object stores if they don't exist
 */
export function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    // Return existing instance if available
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error(`Failed to open database: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create reading_progress store with indexes
      if (!db.objectStoreNames.contains(STORES.PROGRESS)) {
        const progressStore = db.createObjectStore(STORES.PROGRESS, { keyPath: 'bookId' });
        progressStore.createIndex('lastReadAt', 'lastReadAt', { unique: false });
      }

      // Create book_covers store
      if (!db.objectStoreNames.contains(STORES.COVERS)) {
        db.createObjectStore(STORES.COVERS, { keyPath: 'bookId' });
      }

      // Create books store for imported book data (full ProcessedBookResult)
      if (!db.objectStoreNames.contains(STORES.BOOKS)) {
        db.createObjectStore(STORES.BOOKS, { keyPath: 'bookId' });
      }

      // Create daily_reading_stats store for aggregated daily stats
      if (!db.objectStoreNames.contains(STORES.DAILY_READING_STATS)) {
        const dailyStore = db.createObjectStore(STORES.DAILY_READING_STATS, { keyPath: 'date' });
        dailyStore.createIndex('userId', 'userId', { unique: false });
      }

      // Create user_profile store for local user profile data
      if (!db.objectStoreNames.contains(STORES.USER_PROFILE)) {
        db.createObjectStore(STORES.USER_PROFILE, { keyPath: 'userId' });
      }

      // Create user_achievements_local store for local achievement data
      if (!db.objectStoreNames.contains(STORES.USER_ACHIEVEMENTS_LOCAL)) {
        const achievementStore = db.createObjectStore(STORES.USER_ACHIEVEMENTS_LOCAL, { keyPath: 'id' });
        achievementStore.createIndex('userId', 'userId', { unique: false });
        achievementStore.createIndex('achievementId', 'achievementId', { unique: false });
      }

      // Create reading_sessions_local store for Ultra sync
      if (!db.objectStoreNames.contains(STORES.READING_SESSIONS)) {
        const sessionsStore = db.createObjectStore(STORES.READING_SESSIONS, { keyPath: 'id', autoIncrement: true });
        sessionsStore.createIndex('userId', 'userId', { unique: false });
        sessionsStore.createIndex('synced', 'synced', { unique: false });
        sessionsStore.createIndex('sessionDate', 'sessionDate', { unique: false });
      }

      // Create daily_challenges_local store for Ultra sync
      if (!db.objectStoreNames.contains(STORES.DAILY_CHALLENGES_LOCAL)) {
        const challengesStore = db.createObjectStore(STORES.DAILY_CHALLENGES_LOCAL, { keyPath: 'id', autoIncrement: true });
        challengesStore.createIndex('userId', 'userId', { unique: false });
        challengesStore.createIndex('synced', 'synced', { unique: false });
        challengesStore.createIndex('challengeDate', 'challengeDate', { unique: false });
        challengesStore.createIndex('challengeId', 'challengeId', { unique: false });
      }
    };
  });
}

/**
 * Close the database connection
 */
export function closeDB(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

// ============================================================================
// Progress Operations
// ============================================================================

/**
 * Save reading progress for a book
 */
export async function saveProgress(progress: BookReadingProgress): Promise<void> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PROGRESS], 'readwrite');
    const store = transaction.objectStore(STORES.PROGRESS);
    const request = store.put(progress);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error(`Failed to save progress: ${request.error?.message}`));
    };
  });
}

/**
 * Get reading progress for a specific book
 */
export async function getProgress(bookId: string): Promise<BookReadingProgress | null> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PROGRESS], 'readonly');
    const store = transaction.objectStore(STORES.PROGRESS);
    const request = store.get(bookId);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      reject(new Error(`Failed to get progress: ${request.error?.message}`));
    };
  });
}

/**
 * Get all reading progress records
 */
export async function getAllProgress(): Promise<BookReadingProgress[]> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PROGRESS], 'readonly');
    const store = transaction.objectStore(STORES.PROGRESS);
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result || []);
    };

    request.onerror = () => {
      reject(new Error(`Failed to get all progress: ${request.error?.message}`));
    };
  });
}

/**
 * Delete reading progress for a book
 */
export async function deleteProgress(bookId: string): Promise<void> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PROGRESS], 'readwrite');
    const store = transaction.objectStore(STORES.PROGRESS);
    const request = store.delete(bookId);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error(`Failed to delete progress: ${request.error?.message}`));
    };
  });
}

// ============================================================================
// Cover Image Operations
// ============================================================================

/**
 * Cover image storage record
 */
interface CoverRecord {
  bookId: string;
  blob: Blob;
  cachedAt: string;
}

/**
 * Save a cover image for a book
 */
export async function saveCover(bookId: string, blob: Blob): Promise<void> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.COVERS], 'readwrite');
    const store = transaction.objectStore(STORES.COVERS);
    const request = store.put({
      bookId,
      blob,
      cachedAt: new Date().toISOString(),
    } as CoverRecord);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error(`Failed to save cover: ${request.error?.message}`));
    };
  });
}

/**
 * Get a cover image for a book
 */
export async function getCover(bookId: string): Promise<Blob | null> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.COVERS], 'readonly');
    const store = transaction.objectStore(STORES.COVERS);
    const request = store.get(bookId);

    request.onsuccess = () => {
      const result = request.result as CoverRecord | undefined;
      resolve(result?.blob || null);
    };

    request.onerror = () => {
      reject(new Error(`Failed to get cover: ${request.error?.message}`));
    };
  });
}

/**
 * Delete a cover image
 */
export async function deleteCover(bookId: string): Promise<void> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.COVERS], 'readwrite');
    const store = transaction.objectStore(STORES.COVERS);
    const request = store.delete(bookId);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error(`Failed to delete cover: ${request.error?.message}`));
    };
  });
}

// ============================================================================
// Imported Book Data Operations
// ============================================================================

/**
 * Save full book data for an imported book
 * Stores the entire ProcessedBookResult (including chapter content) for offline access
 */
export async function saveBook(book: StoredProcessedBook): Promise<void> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.BOOKS], 'readwrite');
    const store = transaction.objectStore(STORES.BOOKS);
    const request = store.put(book);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error(`Failed to save book: ${request.error?.message}`));
    };
  });
}

/**
 * Get full book data for an imported book
 */
export async function getBook(bookId: string): Promise<StoredProcessedBook | null> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.BOOKS], 'readonly');
    const store = transaction.objectStore(STORES.BOOKS);
    const request = store.get(bookId);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      reject(new Error(`Failed to get book: ${request.error?.message}`));
    };
  });
}

/**
 * Delete an imported book and its associated progress
 */
export async function deleteBook(bookId: string): Promise<void> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.BOOKS, STORES.PROGRESS], 'readwrite');
    transaction.objectStore(STORES.BOOKS).delete(bookId);
    transaction.objectStore(STORES.PROGRESS).delete(bookId);
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => {
      reject(new Error(`Failed to delete book: ${transaction.error?.message}`));
    };
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get overall reading statistics
 */
export async function getOverallStats(): Promise<{
  booksStarted: number;
  totalWordsRead: number;
  chaptersCompleted: number;
}> {
  const allProgress = await getAllProgress();
  
  let totalWordsRead = 0;
  let chaptersCompleted = 0;

  for (const progress of allProgress) {
    totalWordsRead += progress.totalWordsRead;
    chaptersCompleted += progress.chapterProgress.filter(cp => cp.completed).length;
  }

  return {
    booksStarted: allProgress.length,
    totalWordsRead,
    chaptersCompleted,
  };
}

// ============================================================================
// Daily Reading Stats Operations
// ============================================================================

/**
 * Upsert (insert or update) a daily reading stat
 */
export async function upsertDailyStat(stat: DailyReadingStat): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.DAILY_READING_STATS], 'readwrite');
    const store = transaction.objectStore(STORES.DAILY_READING_STATS);
    const request = store.put(stat);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Failed to upsert daily stat: ${request.error?.message}`));
  });
}

/**
 * Get a daily reading stat by date
 */
export async function getDailyStat(date: string): Promise<DailyReadingStat | null> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.DAILY_READING_STATS], 'readonly');
    const store = transaction.objectStore(STORES.DAILY_READING_STATS);
    const request = store.get(date);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Failed to get daily stat: ${request.error?.message}`));
  });
}

/**
 * Get all daily reading stats
 */
export async function getAllDailyStats(): Promise<DailyReadingStat[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.DAILY_READING_STATS], 'readonly');
    const store = transaction.objectStore(STORES.DAILY_READING_STATS);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(new Error(`Failed to get all daily stats: ${request.error?.message}`));
  });
}

/**
 * Get daily stats within a date range
 */
export async function getDailyStatsInRange(startDate: string, endDate: string): Promise<DailyReadingStat[]> {
  const all = await getAllDailyStats();
  return all.filter(d => d.date >= startDate && d.date <= endDate);
}

// ============================================================================
// User Achievements Local Operations
// ============================================================================

/**
 * User achievement stored locally
 */
export interface UserAchievementLocal {
  id?: string; // auto-generated key
  userId: string;
  achievementId: string;
  unlockedAt: string;
  xpEarned: number;
}

/**
 * Get all local achievements for a user
 */
export async function getUserAchievementsLocal(userId: string): Promise<UserAchievementLocal[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.USER_ACHIEVEMENTS_LOCAL], 'readonly');
    const store = transaction.objectStore(STORES.USER_ACHIEVEMENTS_LOCAL);
    const index = store.index('userId');
    const request = index.getAll(userId);
    
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(new Error(`Failed to get user achievements: ${request.error?.message}`));
  });
}

/**
 * Save a user achievement locally
 */
export async function saveUserAchievementLocal(achievement: UserAchievementLocal): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.USER_ACHIEVEMENTS_LOCAL], 'readwrite');
    const store = transaction.objectStore(STORES.USER_ACHIEVEMENTS_LOCAL);
    const request = store.put({
      ...achievement,
      id: achievement.id || `${achievement.userId}_${achievement.achievementId}`,
    });
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Failed to save user achievement: ${request.error?.message}`));
  });
}

/**
 * Get all local achievements (for sync operations)
 */
export async function getAllUserAchievementsLocal(): Promise<UserAchievementLocal[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.USER_ACHIEVEMENTS_LOCAL], 'readonly');
    const store = transaction.objectStore(STORES.USER_ACHIEVEMENTS_LOCAL);
    const request = store.getAll();
    
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(new Error(`Failed to get all user achievements: ${request.error?.message}`));
  });
}

// ============================================================================
// Reading Sessions Local Operations (Ultra Sync)
// ============================================================================

/**
 * Save a reading session locally for later sync to Supabase
 */
export async function saveReadingSession(session: ReadingSessionLocal): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.READING_SESSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.READING_SESSIONS);
    const request = store.add(session);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Failed to save reading session: ${request.error?.message}`));
  });
}

/**
 * Get all reading sessions for a user
 */
export async function getReadingSessionsByUserId(userId: string): Promise<ReadingSessionLocal[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.READING_SESSIONS], 'readonly');
    const store = transaction.objectStore(STORES.READING_SESSIONS);
    const index = store.index('userId');
    const request = index.getAll(userId);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(new Error(`Failed to get reading sessions: ${request.error?.message}`));
  });
}

/**
 * Get unsynced reading sessions for a user
 */
export async function getUnsyncedReadingSessions(userId: string): Promise<ReadingSessionLocal[]> {
  const all = await getReadingSessionsByUserId(userId);
  return all.filter(s => !s.synced);
}

/**
 * Mark a reading session as synced (and optionally store remote ID)
 */
export async function markReadingSessionSynced(id: string, remoteId?: string): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.READING_SESSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.READING_SESSIONS);
    const request = store.get(id);
    request.onsuccess = () => {
      const record = request.result;
      if (record) {
        record.synced = true;
        if (remoteId) record.remoteId = remoteId;
        store.put(record);
      }
      resolve();
    };
    request.onerror = () => reject(new Error(`Failed to mark reading session synced: ${request.error?.message}`));
  });
}

// ============================================================================
// Daily Challenges Local Operations (Ultra Sync)
// ============================================================================

/**
 * Save a daily challenge completion locally for later sync to Supabase
 */
export async function saveDailyChallenge(challenge: DailyChallengeLocal): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.DAILY_CHALLENGES_LOCAL], 'readwrite');
    const store = transaction.objectStore(STORES.DAILY_CHALLENGES_LOCAL);
    const request = store.add(challenge);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Failed to save daily challenge: ${request.error?.message}`));
  });
}

/**
 * Get all daily challenge records for a user
 */
export async function getDailyChallengesByUserId(userId: string): Promise<DailyChallengeLocal[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.DAILY_CHALLENGES_LOCAL], 'readonly');
    const store = transaction.objectStore(STORES.DAILY_CHALLENGES_LOCAL);
    const index = store.index('userId');
    const request = index.getAll(userId);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(new Error(`Failed to get daily challenges: ${request.error?.message}`));
  });
}

/**
 * Get unsynced daily challenge records for a user
 */
export async function getUnsyncedDailyChallenges(userId: string): Promise<DailyChallengeLocal[]> {
  const all = await getDailyChallengesByUserId(userId);
  return all.filter(c => !c.synced);
}

/**
 * Mark a daily challenge record as synced (and optionally store remote ID)
 */
export async function markDailyChallengeSynced(id: string, remoteId?: string): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.DAILY_CHALLENGES_LOCAL], 'readwrite');
    const store = transaction.objectStore(STORES.DAILY_CHALLENGES_LOCAL);
    const request = store.get(id);
    request.onsuccess = () => {
      const record = request.result;
      if (record) {
        record.synced = true;
        if (remoteId) record.remoteId = remoteId;
        store.put(record);
      }
      resolve();
    };
    request.onerror = () => reject(new Error(`Failed to mark daily challenge synced: ${request.error?.message}`));
  });
}

/**
 * Clear all data from the database
 */
export async function clearAllData(): Promise<void> {
  const db = await initDB();
  
  const stores = [STORES.PROGRESS, STORES.COVERS];
  
  await Promise.all(stores.map(storeName => {
    return new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error(`Failed to clear ${storeName}: ${request.error?.message}`));
      };
    });
  }));
}

/**
 * Export all data for backup/sync
 */
export async function exportAllData(): Promise<{
  progress: BookReadingProgress[];
  exportedAt: string;
}> {
  const progress = await getAllProgress();

  return {
    progress,
    exportedAt: new Date().toISOString(),
  };
}

/**
 * Import data from a backup
 */
export async function importData(data: {
  progress?: BookReadingProgress[];
}): Promise<{ progressImported: number }> {
  const db = await initDB();
  
  let progressImported = 0;

  // Import progress
  if (data.progress && data.progress.length > 0) {
    const transaction = db.transaction([STORES.PROGRESS], 'readwrite');
    const store = transaction.objectStore(STORES.PROGRESS);
    
    await Promise.all(data.progress.map(prog => {
      return new Promise<void>((resolve, reject) => {
        const request = store.put(prog);
        request.onsuccess = () => {
          progressImported++;
          resolve();
        };
        request.onerror = () => {
          reject(new Error(`Failed to import progress: ${request.error?.message}`));
        };
      });
    }));
  }

  return { progressImported };
}
